/** @jsxImportSource preact */
import { useEffect, useState } from "preact/hooks";
import { addSeconds, differenceInSeconds, parseISO } from "date-fns";
import { customerOrderEmailSchema } from "@go-novus/admin-api";
import type {
  CustomerAuth,
  VerifyEmailAction,
} from "../../protocol/customer-auth";
import * as m from "../../paraglide/messages.js";
import type { WidgetTokens } from "../theme";
import { TextField } from "../form/text-field";
import { FormButton } from "../form/form-button";
import { FORM_STYLES, formVars } from "../form/form-styles";
import { VerifyEmailCard, type WidgetVerifyStage } from "./verify-email-card";

const NO_AUTOCOMPLETE = "off";

/** 表单只存组件内存；成功或卸载后清除，刷新从 BFF 的短期挑战恢复。 */
export function EmailVerificationFlow({
  tokens,
  playground,
  auth,
  onVerify,
  onContactStore,
}: {
  tokens: WidgetTokens;
  playground?: boolean;
  auth?: CustomerAuth;
  onVerify: VerifyEmailAction;
  onContactStore?: () => void;
}) {
  const pending = auth?.pending_email_code;
  const [challengeId, setChallengeId] = useState(pending?.challenge_id);
  const [email, setEmail] = useState(auth?.login_hint_email ?? "");
  const [code, setCode] = useState("");
  const [stage, setStage] = useState<WidgetVerifyStage>(
    pending ? (pending.tries_left ? "codeSent" : "locked") : "initial",
  );
  const [busy, setBusy] = useState(false);
  const [verified, setVerified] = useState(auth?.email_code === "verified");
  const [emailError, setEmailError] = useState(false);
  const [notice, setNotice] = useState<string>();
  const [triesLeft, setTriesLeft] = useState(pending?.tries_left);
  const [retryAt, setRetryAt] = useState(() =>
    addSeconds(new Date(), pending?.retry_after ?? 0),
  );
  const [cooldown, setCooldown] = useState(pending?.retry_after ?? 0);
  useEffect(() => {
    const timer = setInterval(() => {
      setCooldown(Math.max(0, differenceInSeconds(retryAt, new Date())));
    }, 1000);
    return () => {
      clearInterval(timer);
    };
  }, [retryAt]);
  useEffect(() => {
    if (!pending || !challengeId) return;
    const timer = setTimeout(
      () => {
        setStage("codeExpired");
      },
      Math.max(
        0,
        differenceInSeconds(parseISO(pending.expires_at), new Date()) * 1000,
      ),
    );
    return () => {
      clearTimeout(timer);
    };
  }, [pending?.expires_at, challengeId]);
  useEffect(() => {
    if (cooldown === 0 && stage === "locked") setStage("codeExpired");
    if (cooldown === 0 && stage === "cooldown") setStage("codeSent");
  }, [stage, cooldown]);
  const run = async (operation: "send" | "verify" | "playground") => {
    if (busy) return;
    if (operation === "send" && challengeId && !email) {
      setChallengeId(undefined);
      setStage("initial");
      return;
    }
    const parsed = customerOrderEmailSchema.safeParse(email);
    setEmailError(!parsed.success && !(operation === "verify" && challengeId));
    if (!parsed.success && !(operation === "verify" && challengeId)) return;
    if (operation === "verify" && !/^\d{6}$/.test(code)) return;
    setBusy(true);
    try {
      const result = await onVerify({
        operation,
        email:
          operation === "verify" && challengeId
            ? ""
            : parsed.success
              ? parsed.data
              : "",
        ...(operation === "verify" && challengeId
          ? { challenge_id: challengeId }
          : {}),
        ...(operation === "verify" ? { code } : {}),
      });
      if (result.status === "failed") {
        setStage(operation === "verify" ? "codeSent" : "sendFailed");
        setNotice(m.widget_verify_failed());
        return;
      }
      setTriesLeft(result.tries_left);
      setRetryAt(addSeconds(new Date(), result.retry_after));
      setCooldown(result.retry_after);
      setNotice(undefined);
      switch (result.status) {
        case "verified":
          setEmail("");
          setCode("");
          setVerified(true);
          break;
        case "accepted":
          setChallengeId(undefined);
          setCode("");
          setStage("codeSent");
          break;
        case "unknown":
          setChallengeId(undefined);
          setStage("codeSent");
          setNotice(m.widget_verify_unknown());
          break;
        case "wrong":
          setStage("codeWrong");
          break;
        case "expired":
          setStage("codeExpired");
          break;
        case "locked":
          setStage("locked");
          setNotice(m.widget_verify_locked());
          break;
        case "cooldown":
          setStage("cooldown");
          break;
        case "not_configured":
          setStage("sendFailed");
          setNotice(m.widget_verify_unavailable());
          break;
        default:
          setStage(operation === "verify" ? "codeSent" : "sendFailed");
          setNotice(m.widget_verify_failed());
      }
    } catch {
      setStage(operation === "verify" ? "codeSent" : "sendFailed");
      setNotice(m.widget_verify_failed());
    } finally {
      setBusy(false);
    }
  };
  if (verified)
    return (
      <>
        <style>{FORM_STYLES}</style>
        <div role="status">{m.widget_verify_success()}</div>
        {onContactStore && (
          <FormButton
            fullWidth
            tokens={tokens}
            variant="ghost"
            onClick={onContactStore}
          >
            {m.widget_verify_contact_store()}
          </FormButton>
        )}
      </>
    );
  if (playground)
    return (
      <div style={formVars(tokens)}>
        <style>{FORM_STYLES}</style>
        <TextField
          tokens={tokens}
          type="email"
          autoComplete={NO_AUTOCOMPLETE}
          label={m.widget_verify_email_label()}
          value={email}
          onValueChange={setEmail}
          disabled={busy}
          error={emailError ? m.widget_verify_email_invalid() : undefined}
        />
        {notice && <div role="status">{notice}</div>}
        <FormButton
          tokens={tokens}
          disabled={busy}
          onClick={() => {
            void run("playground");
          }}
        >
          {m.widget_verify_playground_action()}
        </FormButton>
        {onContactStore && (
          <FormButton
            tokens={tokens}
            variant="ghost"
            onClick={onContactStore}
            disabled={busy}
          >
            {m.widget_verify_contact_store()}
          </FormButton>
        )}
      </div>
    );
  return (
    <VerifyEmailCard
      onContactStore={onContactStore}
      tokens={tokens}
      email={
        challengeId && pending && stage !== "initial" && stage !== "sendFailed"
          ? pending.masked_email
          : email
      }
      code={code}
      stage={stage}
      busy={busy}
      notice={notice}
      emailError={emailError}
      triesLeft={triesLeft}
      cooldown={
        cooldown
          ? `${String(Math.floor(cooldown / 60))}:${String(cooldown % 60).padStart(2, "0")}`
          : undefined
      }
      onEmailChange={setEmail}
      onCodeChange={setCode}
      onSendCode={() => {
        void run("send");
      }}
      onResend={() => {
        void run("send");
      }}
      onVerify={() => {
        void run("verify");
      }}
    />
  );
}
